/**
 * A turn that did not arrive (and the recovery the turn machine built).
 *
 * The hard part of this component is not the styling, it is that **"it failed" and "you may
 * try again" are different questions**, and conflating them is expensive. A turn costs ~$0.13 and is not
 * idempotent, and the server finishes and commits a turn whose client went away — so a dropped
 * stream is not a failed turn, it is a turn this client stopped watching. Offering a retry
 * there is offering to pay twice for one chapter.
 *
 * So the retry affordance is gated on exactly one thing: `mayStillBeRunning` (surfaced as
 * `resolving`). While that is true the only honest instruction is *wait, and do not act* — the
 * recovery poll in `useTurn` is asking the server every five seconds and will clear this on
 * its own the moment the chapter lands. When it is false the turn provably wrote nothing, and
 * retrying replays the same `Idempotency-Key` (P2) rather than running a second turn.
 */
import type { ApiError } from '@/api/client';
import { describeApiError } from '@/api/errorMessages';
import { useT } from '@/i18n/context';
import type { UiKey } from '@/i18n/en.generated';
import { intlLocale, type Translate } from '@/i18n/t';
import type { ReconcileResult } from '@/turn/reconcile';

interface TurnFailureProps {
  error: ApiError;
  /** The server's answer about a turn we lost sight of. Null until it has been asked. */
  recovery: ReconcileResult | null;
  /** `mayStillBeRunning`: a turn may still be resolving somewhere, so do not offer to act. */
  resolving: boolean;
  onRetry: () => void;
  onDismiss: () => void;
  /**
   * Opens feedback carrying P5's `request_id`. Null when there is nothing worth
   * reporting — `isReportable` is the caller's rule, and it is false for a failure that is
   * the player's own situation rather than ours.
   */
  onReport?: (() => void) | null;
}

export function TurnFailure({
  error,
  recovery,
  resolving,
  onRetry,
  onDismiss,
  onReport = null,
}: TurnFailureProps) {
  const t = useT();
  const active = error.activeTurn;

  return (
    <div
      role="alert"
      className="border-border bg-panel mt-[var(--space-chapter)] border-l-2 p-4"
      style={{ fontSize: 'var(--text-meta)' }}
    >
      <p className="text-fg">{describeApiError(t, error)}</p>

      {active ? (
        <p className="text-fg-muted mt-2 font-mono">
          {t('ui.turn.started_at', {
            chapter: active.chapter_number,
            time: formatStartedAt(t, active.started_at),
          })}
        </p>
      ) : null}

      {resolving ? (
        <p className="text-fg-faint mt-2">{t(RECOVERY_NOTES[recovery ?? 'pending'])}</p>
      ) : (
        <div className="mt-3 flex gap-3">
          <button
            type="button"
            onClick={onRetry}
            className="border-border text-fg hover:border-accent hover:text-accent border px-3 py-1.5 font-mono transition-colors"
          >
            {t('ui.common.retry')}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="text-fg-faint hover:text-fg-muted px-2 py-1.5 font-mono transition-colors"
          >
            {t('ui.turn.dismiss')}
          </button>
          {onReport ? (
            <button
              type="button"
              onClick={onReport}
              className="text-fg-faint hover:text-fg-muted ml-auto px-2 py-1.5 font-mono transition-colors"
            >
              {t('ui.feedback.report_error')}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * What the poll is doing, while it is doing it. Each one ends in the same instruction because
 * the instruction is the same: the turn is the server's now, and this page is a spectator.
 */
const RECOVERY_NOTES: Record<ReconcileResult | 'pending', UiKey> = {
  pending: 'ui.turn.recovery.pending',
  in_flight: 'ui.turn.recovery.in_flight',
  unknown: 'ui.turn.recovery.unknown',
  // Both of these clear the store rather than rendering, so they are unreachable in practice
  // — present so the record is exhaustive and a new result cannot render as blank.
  landed: 'ui.turn.recovery.landed',
  lost: 'ui.turn.recovery.lost',
};

function formatStartedAt(t: Translate, iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? iso
    : at.toLocaleTimeString(intlLocale(t.locale), { hour: '2-digit', minute: '2-digit' });
}
